import React, { memo, useEffect, useState } from 'react';
import { SearchPagination } from './SearchPagination';

interface SearchResult {
    id: number;
    name: string;
    url: string;
    metaDescription?: string;
    contentType: string;
    createdDate: string;
    categories: string[];
    pageType?: string;
}

interface SearchResultsProps {
    results: SearchResult[];
    loading: boolean;
    totalResults: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onCategoryFilter?: (category: string, checked: boolean) => void;
    selectedCategories?: string[];
}

// Memoize the component to prevent unnecessary re-renders
export const SearchResults: React.FC<SearchResultsProps> = memo(({
    results,
    loading,
    totalResults,
    currentPage,
    pageSize,
    totalPages,
    onPageChange,
    onCategoryFilter,
    selectedCategories = []
}) => {
    const [showLoading, setShowLoading] = useState(false);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        
        if (loading) {
            // Only show loading spinner after 500ms delay
            timeoutId = setTimeout(() => {
                setShowLoading(true);
            }, 500);
        } else {
            // Immediately hide loading when not loading
            setShowLoading(false);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [loading]);

    if (loading && showLoading) {
        return (
            <div className="search-loading" aria-live="polite">
                <svg className="svg-icon">
                    <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#icon-loading" />
                </svg>
            </div>
        );
    }

    if (results.length === 0) {
        return <p>No Results Found</p>;
    }

    // Filter out items without valid names as an additional safety check
    const validResults = results.filter(result => 
        result.name && result.name.trim().length > 0
    );

    return (
        <>
            <ul className="search-page-results">
                {validResults.map((result) => (
                    <li key={result.id} className="search-page-results-result stack-md">
                        <p>{new Date(result.createdDate).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                        })}</p>

                        <a className="h5-style" href={result.url}>
                            {result.name}
                        </a>

                        {result.metaDescription && (
                            <div dangerouslySetInnerHTML={{ __html: result.metaDescription }} />
                        )}

                        {result.categories && result.categories.length > 0 && (
                            <ul className='search-page-results-result-tags'>
                                <li>Tags:</li>
                                {result.categories.map((category, index) => {
                                    const isSelected = selectedCategories.includes(category);
                                    return (
                                        <li key={index}>
                                            <a 
                                                className={`green-hyperlink ${isSelected ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (onCategoryFilter) {
                                                        onCategoryFilter(category, !isSelected);
                                                    }
                                                }}
                                                style={{ 
                                                    cursor: 'pointer',
                                                    fontWeight: isSelected ? 'bold' : 'normal'
                                                }}
                                            >
                                                {category}
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {result.pageType && (
                            <p className='hide'>{result.pageType}</p>
                        )}
                    </li>
                ))}
            </ul>  

            <SearchPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
            />    
        </>
    );
});

SearchResults.displayName = 'SearchResults';
